import { cloneElement, isValidElement } from 'react';
import { useInsforge } from '../provider/InsforgeProvider';

export interface SignOutButtonProps {
  children?: React.ReactNode;
  className?: string;
  afterSignOutUrl?: string;
}

/**
 * SignOutButton Component
 *
 * A simple unstyled button that signs out the current user.
 * If children is a valid React element, it will be rendered with the sign-out logic attached.
 *
 * @example
 * ```tsx
 * <SignOutButton />
 * ```
 *
 * @example
 * ```tsx
 * <SignOutButton afterSignOutUrl="/goodbye">
 *   <button className="custom-button">Logout</button>
 * </SignOutButton>
 * ```
 */
export function SignOutButton({ children, className, afterSignOutUrl = '/' }: SignOutButtonProps) {
  const { signOut } = useInsforge();

  const handleClick = async () => {
    await signOut();
    window.location.href = afterSignOutUrl;
  };

  if (children && isValidElement(children)) {
    const originalOnClick = (children.props as { onClick?: (e: React.MouseEvent) => void })
      ?.onClick;

    return cloneElement(children, {
      onClick: async (e: React.MouseEvent) => {
        await handleClick();
        if (originalOnClick) {
          originalOnClick(e);
        }
      },
    } as Record<string, unknown>);
  }

  // Default button
  return (
    <button type="button" className={className} onClick={() => void handleClick()}>
      {children || 'Sign out'}
    </button>
  );
}
